/**
 * Jagad Jawa — Modul Domain: Share Card Weton
 * Generator kartu weton estetis untuk media sosial & WhatsApp viral loop
 * dilengkapi ornamen budaya Nusantara, ringkasan weton, dan petikan pitutur luhur.
 */

import { getDayInfo, getTanggalJawaLengkap, getNeptu, getPranataMangsaLengkap, BULAN_MASEHI } from './kalender-engine.js';
import { showToast, copyToClipboard } from '../../ui/toast.js';
import { drawShareCardQrCode } from '../../ui/ux-enhancements.js';

const KUTIPAN_BIJAK = [
  { jawa: "Urip iku urup.", arti: "Hidup itu hendaknya menyala dan memberi manfaat bagi sesama." },
  { jawa: "Sura dira jayaningrat lebur dening pangastuti.", arti: "Segala angkara murka dan kekerasan hati akan lebur oleh kelembutan dan kasih sayang." },
  { jawa: "Memayu hayuning bawana.", arti: "Memperindah dan menjaga keselarasan alam semesta." },
  { jawa: "Aja adigang, adigung, adiguna.", arti: "Jangan menyombongkan kekuatan, kekuasaan, atau kepandaian." },
  { jawa: "Ngluruk tanpa bala, menang tanpa ngasorake.", arti: "Berjuang tanpa mengandalkan massa, menang tanpa merendahkan lawan." },
  { jawa: "Alang-alang dudu aling-aling.", arti: "Rintangan hidup bukanlah penghalang untuk mencapai kemuliaan budi." }
];

export function getCulturalQuote(seed = 0) {
  return KUTIPAN_BIJAK[Math.abs(seed) % KUTIPAN_BIJAK.length];
}

/**
 * Format teks WhatsApp yang rapi dan elegan untuk dibagikan ke keluarga / rekan.
 * @param {number} y 
 * @param {number} m 
 * @param {number} d 
 * @returns {string}
 */
export function buildWhatsAppShareText(y, m, d) {
  const tglJawa = getTanggalJawaLengkap(y, m, d);
  const pm = getPranataMangsaLengkap(d, m);
  const quote = getCulturalQuote(y * 10000 + m * 100 + d);

  const tglMasehiStr = `${d} ${BULAN_MASEHI[m - 1]} ${y}`;

  return `✨ *WETON & PETUNGAN JAWA* ✨\n` +
    `_Kadhudhah lumantar Jagad Jawa — Budaya Luhur Nusantara_\n\n` +
    `📅 *Tanggal Masehi:* ${tglMasehiStr}\n` +
    `🌟 *Weton:* *${tglJawa.dino} ${tglJawa.pas}*\n` +
    `🔢 *Neptu:* ${tglJawa.neptu} (${tglJawa.dino} + ${tglJawa.pas})\n` +
    `📜 *Penanggalan Jawa:* ${tglJawa.tglJawa} ${tglJawa.bulanJawa} ${tglJawa.tahunAJ} AJ (Tahun ${tglJawa.tahunSiklus})\n` +
    `🪐 *Wuku:* ${tglJawa.wukuName} (${tglJawa.wukuNo}/30)\n` +
    `🌾 *Pranata Mangsa:* ${pm.nama} — _${pm.musimTani}_\n` +
    `🪶 *Candrasangkala:* "${pm.candrasangkala}"\n\n` +
    `💬 *Pitutur Luhur:* \n"${quote.jawa}"\n_${quote.arti}_\n\n` +
    `Telusuri petungan weton lan nujum pribadimu ing:\nhttps://jagad-jawa.web.app`;
}

/**
 * Membuka WhatsApp dengan teks weton siap kirim.
 * @param {number} y 
 * @param {number} m 
 * @param {number} d 
 */
export function shareWetonViaWhatsApp(y, m, d) {
  const text = buildWhatsAppShareText(y, m, d);
  const encoded = encodeURIComponent(text);
  const waUrl = `https://api.whatsapp.com/send?text=${encoded}`;
  window.open(waUrl, '_blank');
  showToast('Membuka WhatsApp untuk membagikan weton...');
}

/**
 * Menggambar Share Card Weton ke HTML5 Canvas secara murni.
 * @param {HTMLCanvasElement} canvas 
 * @param {number} y 
 * @param {number} m 
 * @param {number} d 
 * @param {string} [customNama='']
 * @param {'standard'|'story'|'feed'} [ratio='standard']
 */
export function drawWetonCardToCanvas(canvas, y, m, d, customNama = '', ratio = 'standard') {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const tglJawa = getTanggalJawaLengkap(y, m, d);
  const pm = getPranataMangsaLengkap(d, m);
  const quote = getCulturalQuote(y * 10000 + m * 100 + d);
  const tglMasehiStr = `${d} ${BULAN_MASEHI[m - 1]} ${y}`;

  let width = 640;
  let height = 800;
  if (ratio === 'story') {
    width = 1080;
    height = 1920;
  } else if (ratio === 'feed') {
    width = 1080;
    height = 1350;
  } else if (ratio === 'square') {
    width = 1080;
    height = 1080;
  }

  const scale = width / 640;
  canvas.width = width;
  canvas.height = height;

  // 1. Background Gradient Gelap Tradisi
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, '#0f141d');
  grad.addColorStop(0.5, '#182030');
  grad.addColorStop(1, '#090d14');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // 2. Ornamen Bingkai Emas (Dual Border)
  ctx.strokeStyle = '#d4af37';
  ctx.lineWidth = 4;
  ctx.strokeRect(20, 20, width - 40, height - 40);

  ctx.strokeStyle = 'rgba(212, 175, 55, 0.35)';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(28, 28, width - 56, height - 56);

  // 3. Header Kop Tradisi Jawa
  ctx.fillStyle = '#fdf0cd';
  ctx.textAlign = 'center';
  ctx.font = 'bold 12px monospace';
  ctx.fillText('PAWIYATAN LUHUR · JAGAD JAWA', width / 2, 58);

  ctx.fillStyle = '#d4af37';
  ctx.font = 'bold 22px serif';
  const cardTitle = (customNama && customNama.trim() !== '') ? `WETON · ${customNama.trim().toUpperCase()}` : 'KARTU WETON NUSANTARA';
  ctx.fillText(cardTitle.length > 28 ? cardTitle.substring(0, 26) + '...' : cardTitle, width / 2, 88);

  ctx.strokeStyle = 'rgba(212, 175, 55, 0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(120, 102);
  ctx.lineTo(width - 120, 102);
  ctx.stroke();

  // 4. Tanggal Masehi
  ctx.fillStyle = '#cbd5e1';
  ctx.font = '14px sans-serif';
  ctx.fillText(tglMasehiStr, width / 2, 136);

  // 5. Hero Weton Box
  ctx.fillStyle = 'rgba(30, 41, 59, 0.7)';
  ctx.beginPath();
  ctx.roundRect(50, 160, width - 100, 150, 16);
  ctx.fill();
  ctx.strokeStyle = 'rgba(212, 175, 55, 0.6)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Tulisan Weton Utama
  ctx.fillStyle = '#f6ecd2';
  ctx.font = 'bold 38px serif';
  ctx.fillText(`${tglJawa.dino} ${tglJawa.pas}`, width / 2, 222);

  // Badge Neptu & Wuku
  ctx.fillStyle = '#d4af37';
  ctx.font = 'bold 16px sans-serif';
  ctx.fillText(`Neptu ${tglJawa.neptu}  ·  Wuku ${tglJawa.wukuName}`, width / 2, 260);

  ctx.fillStyle = '#94a3b8';
  ctx.font = '12px sans-serif';
  ctx.fillText(`${tglJawa.tglJawa} ${tglJawa.bulanJawa} ${tglJawa.tahunAJ} AJ · Windu ${tglJawa.namaWindu}`, width / 2, 288);

  // 6. Section Pranata Mangsa & Musim Tani
  ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
  ctx.beginPath();
  ctx.roundRect(50, 330, width - 100, 180, 16);
  ctx.fill();
  ctx.strokeStyle = 'rgba(45, 212, 191, 0.4)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.fillStyle = '#2dd4bf';
  ctx.font = 'bold 11px monospace';
  ctx.fillText('KOSMOLOGI PRANATA MANGSA', width / 2, 360);

  ctx.fillStyle = '#f1f5f9';
  ctx.font = 'bold 20px serif';
  ctx.fillText(pm.nama, width / 2, 392);

  ctx.fillStyle = '#38bdf8';
  ctx.font = 'italic 13px serif';
  ctx.fillText(`"${pm.candrasangkala}"`, width / 2, 420);

  ctx.fillStyle = '#e2e8f0';
  ctx.font = '12px sans-serif';
  ctx.fillText(`Mangsa: ${pm.musimTani}`, width / 2, 450);

  ctx.fillStyle = '#94a3b8';
  ctx.font = '11px sans-serif';
  // Wrap text for pratandha
  const pratandha = `Tandha Alam: ${pm.pratandhaAlam}`;
  ctx.fillText(pratandha.length > 60 ? pratandha.substring(0, 58) + '...' : pratandha, width / 2, 480);

  // 7. Section Pitutur Luhur
  ctx.fillStyle = 'rgba(30, 27, 20, 0.85)';
  ctx.beginPath();
  ctx.roundRect(50, 530, width - 100, 140, 16);
  ctx.fill();
  ctx.strokeStyle = 'rgba(212, 175, 55, 0.5)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.fillStyle = '#d4af37';
  ctx.font = 'bold 11px monospace';
  ctx.fillText('FALSAFAH & PITUTUR LUHUR', width / 2, 560);

  ctx.fillStyle = '#fde68a';
  ctx.font = 'bold italic 18px serif';
  ctx.fillText(`"${quote.jawa}"`, width / 2, 595);

  ctx.fillStyle = '#cbd5e1';
  ctx.font = '12px sans-serif';
  ctx.fillText(quote.arti, width / 2, 630);

  // 8. Footer Watermark & QR Code
  ctx.fillStyle = '#64748b';
  ctx.font = '11px monospace';
  ctx.textAlign = 'left';
  ctx.fillText('jagad-jawa.web.app · Lestari Budaya Luhur Nusantara', 45, height - 35);
  ctx.fillText('Pituduh Gesang & Petungan Kalender Tradisi', 45, height - 20);

  // Gambar QR Code visual di pojok kanan bawah
  drawShareCardQrCode(ctx, width - 85, height - 85, 55);
}

/**
 * Menampilkan modal share card weton.
 * @param {number} y 
 * @param {number} m 
 * @param {number} d 
 */
export function openWetonShareModal(y, m, d, customNama = '') {
  if (!y || !m || !d) {
    const now = new Date();
    y = y || now.getFullYear();
    m = m || (now.getMonth() + 1);
    d = d || now.getDate();
  }

  const modal = document.getElementById('modalShareCardWeton');
  const canvas = document.getElementById('shareCardCanvas');
  if (!modal || !canvas) return;

  const dateInput = document.getElementById('shareCardDateInput');
  if (dateInput) {
    dateInput.value = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  const namaInput = document.getElementById('shareCardNamaInput');
  if (namaInput && customNama) {
    namaInput.value = customNama;
  }

  let currentRatio = 'standard';

  const rerender = () => {
    let curY = y, curM = m, curD = d;
    if (dateInput && dateInput.value) {
      const parts = dateInput.value.split('-').map(Number);
      if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
        curY = parts[0]; curM = parts[1]; curD = parts[2];
      }
    }
    const curNama = namaInput ? namaInput.value.trim() : '';
    drawWetonCardToCanvas(canvas, curY, curM, curD, curNama, currentRatio);

    const btnWa = document.getElementById('btnShareWetonWA');
    if (btnWa) {
      btnWa.onclick = () => shareWetonViaWhatsApp(curY, curM, curD);
    }

    const btnDownload = document.getElementById('btnDownloadShareCardPng');
    if (btnDownload) {
      btnDownload.onclick = () => downloadShareCardPng(curY, curM, curD, curNama);
    }

    // Ratio toggle buttons
    document.querySelectorAll('.share-card-ratio-btn').forEach(btn => {
      if (btn.dataset.ratio === currentRatio) {
        btn.className = 'share-card-ratio-btn px-3 py-1.5 rounded-xl bg-gradient-to-r from-sogan-600 to-prada text-keraton font-bold text-xs shadow-md transition';
      } else {
        btn.className = 'share-card-ratio-btn px-3 py-1.5 rounded-xl bg-sogan-900 border border-sogan-800 text-sogan-300 hover:text-prada text-xs transition';
      }
      btn.onclick = () => {
        currentRatio = btn.dataset.ratio || 'standard';
        rerender();
      };
    });

    const btnCopy = document.getElementById('btnCopyShareText');
    if (btnCopy) {
      btnCopy.onclick = () => {
        const text = buildWhatsAppShareText(curY, curM, curD);
        copyToClipboard(text, 'Teks weton kasil disalin kanggé WhatsApp!');
      };
    }
  };

  if (dateInput) dateInput.onchange = rerender;
  if (namaInput) namaInput.oninput = rerender;

  rerender();

  modal.classList.remove('hidden');
  modal.classList.add('flex');
  if (typeof document !== 'undefined' && document.body) {
    document.body.style.overflow = 'hidden';
  }
}

export function closeWetonShareModal() {
  const modal = document.getElementById('modalShareCardWeton');
  if (modal) {
    modal.classList.add('hidden');
    modal.classList.remove('flex');
  }
  if (typeof document !== 'undefined' && document.body) {
    document.body.style.overflow = 'auto';
  }
}

export function downloadShareCardPng(y, m, d, customNama = '') {
  const canvas = document.getElementById('shareCardCanvas');
  if (!canvas) return;

  const tglJawa = getTanggalJawaLengkap(y, m, d);
  const cleanNama = customNama ? `-${customNama.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
  const link = document.createElement('a');
  link.download = `Weton${cleanNama}-${tglJawa.dino}-${tglJawa.pas}-${d}-${m}-${y}.png`;
  link.href = canvas.toDataURL('image/png');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  showToast('Kartu weton kasil diunduh minangka gambar PNG!');
}
